import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  SKILL_DESCRIPTION_MAX_LENGTH,
  SkillAccessibility,
  SkillDraft,
  SkillSummary,
  SkillVersionRecord,
  validateSkillName,
} from "@/shared/domain/skills";
import {
  useCreateSkill,
  useCreateVersion,
  useKnownSkillTags,
  useSkillVersions,
  useUpdateVersion,
} from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./SkillEditor.module.css";
import { TagsAutocomplete } from "./TagsAutocomplete";

/** Trim, drop empties, and dedupe (case-insensitive on the original casing). */
function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * If null, the editor is in "create new skill" mode (which creates the
   * skill row plus its first version atomically). Otherwise the editor edits
   * an existing version in place — strictly version-level fields, since
   * skill-level metadata (name, accessibility, tags) lives in
   * :class:`SkillMetadataEditor` to keep the edit-version dialog focused.
   */
  summary: SkillSummary | null;
  /**
   * The version number to load when editing. Required when ``summary`` is
   * non-null; ignored in create mode.
   */
  editingVersion?: number | null;
  /**
   * Seed values for create mode (``summary === null``), e.g. a draft handed
   * over from the `client_create_skill` chat tool. Ignored when editing an
   * existing version. Read once when the dialog (re)opens; the user edits
   * freely from there.
   */
  initialCreateValues?: SkillDraft | null;
  orgId: string | undefined;
}

interface CreateFormState {
  // Skill-level fields — only relevant in create mode (skill + version 1 are
  // made atomically). Edit-existing-skill flows go through SkillMetadataEditor.
  name: string;
  accessibility: SkillAccessibility;
  tags: string[];
  // Version-level fields — used in both create and edit-version.
  description: string;
  body: string;
}

interface EditFormState {
  description: string;
  body: string;
}

const EMPTY_CREATE: CreateFormState = {
  name: "",
  accessibility: "private",
  tags: [],
  description: "",
  body: "",
};

const EMPTY_EDIT: EditFormState = {
  description: "",
  body: "",
};

function latestVersion(
  versions: SkillVersionRecord[] | undefined,
): SkillVersionRecord | null {
  if (!versions || versions.length === 0) {
    return null;
  }
  return versions.reduce((a, b) => (a.version > b.version ? a : b));
}

function pickVersion(
  versions: SkillVersionRecord[] | undefined,
  requested: number | null | undefined,
): SkillVersionRecord | null {
  if (requested !== null && requested !== undefined) {
    return versions?.find((v) => v.version === requested) ?? null;
  }
  return latestVersion(versions);
}

function seedEditForm(target: SkillVersionRecord | null): EditFormState {
  if (target === null) {
    return EMPTY_EDIT;
  }
  return {
    description: target.description,
    body: target.body,
  };
}

/**
 * Seed the create form from an optional draft, filling every field the draft
 * leaves out with the empty-form default. A null / absent draft yields the
 * plain empty form, so the manual "Create Skill" button and the chat-tool
 * handoff share one code path.
 */
function seedCreateForm(draft: SkillDraft | null | undefined): CreateFormState {
  if (!draft) {
    return EMPTY_CREATE;
  }
  return {
    name: draft.name ?? EMPTY_CREATE.name,
    accessibility: draft.accessibility ?? EMPTY_CREATE.accessibility,
    tags: draft.tags ?? EMPTY_CREATE.tags,
    description: draft.description ?? EMPTY_CREATE.description,
    body: draft.body ?? EMPTY_CREATE.body,
  };
}

export function SkillEditor({
  open,
  onClose,
  summary,
  editingVersion,
  initialCreateValues,
  orgId,
}: Props) {
  const isCreating = summary === null;

  // Two distinct form-state shapes — one per mode — so a stale field from the
  // wrong mode can't silently leak into a request body.
  const [createForm, setCreateForm] =
    React.useState<CreateFormState>(EMPTY_CREATE);
  const [editForm, setEditForm] = React.useState<EditFormState>(EMPTY_EDIT);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const versionsQuery = useSkillVersions(summary?.skill.skill_id);
  const knownTagsQuery = useKnownSkillTags(orgId);
  const createSkill = useCreateSkill(orgId);
  const createVersion = useCreateVersion(orgId);
  const updateVersion = useUpdateVersion(orgId);

  // Reseed only when the dialog is (re)opened, the target skill changes, or
  // the requested editing version changes — NOT on every refetch of versions.
  // Listing ``versionsQuery.data`` in deps used to clobber the user's typing
  // on a tab focus or post-mutation refetch. ``initialCreateValues`` is also
  // excluded: it's a handoff seed read once at open-time, so reseeding mid-edit
  // if the caller's reference changed would clobber the user's edits.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    if (summary === null) {
      setCreateForm(seedCreateForm(initialCreateValues));
    } else {
      const target = pickVersion(versionsQuery.data, editingVersion);
      setEditForm(seedEditForm(target ?? summary.latest_version));
    }
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: see comment above
  }, [open, summary?.skill.skill_id, editingVersion]);

  const target = pickVersion(versionsQuery.data, editingVersion);

  function updateCreate<K extends keyof CreateFormState>(
    key: K,
    value: CreateFormState[K],
  ) {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateEdit<K extends keyof EditFormState>(
    key: K,
    value: EditFormState[K],
  ) {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setErrorMessage(null);
    if (isCreating) {
      const trimmedName = createForm.name.trim();
      const nameError = validateSkillName(trimmedName);
      if (nameError !== null) {
        setErrorMessage(nameError);
        return;
      }
      // TagsAutocomplete commits pending text on blur — by the time this
      // handler runs (after the Save button's click), any typed-but-not-
      // Enter'd tag is already in createForm.tags. Just dedupe.
      const initialTags = normalizeTags(createForm.tags);
      try {
        await createSkill.mutateAsync({
          name: trimmedName,
          accessibility: createForm.accessibility,
          description: createForm.description,
          body: createForm.body,
          tags: initialTags.length > 0 ? initialTags : undefined,
        });
        onClose();
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : "Failed to save.");
      }
      return;
    }
    if (summary === null || target === null) {
      // versionsQuery hasn't resolved yet — disable the button via `isSaving`
      // below so this is unreachable; defensive return for type-narrowing.
      return;
    }
    try {
      // Version body / description are now the only mutable fields per version;
      // AI visibility lives in the per-user subscription, not on the version.
      await updateVersion.mutateAsync({
        skillId: summary.skill.skill_id,
        version: target.version,
        request: {
          description: editForm.description,
          body: editForm.body,
        },
      });
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to save.");
    }
  }

  async function handleCreateNewVersion() {
    setErrorMessage(null);
    if (isCreating || summary === null || target === null) {
      return;
    }
    try {
      await createVersion.mutateAsync({
        skillId: summary.skill.skill_id,
        request: {
          description: editForm.description,
          body: editForm.body,
        },
      });
      onClose();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to create version.",
      );
    }
  }

  const isMutating =
    createSkill.isPending || createVersion.isPending || updateVersion.isPending;
  // While editing, we update the version selected by ``editingVersion``; if
  // versions haven't loaded yet, Save would silently no-op. Disable until they
  // arrive.
  const editingButVersionsNotReady = !isCreating && target === null;
  const isSaving = isMutating || editingButVersionsNotReady;

  // Compute the live name-validation error only in create mode — edit mode
  // doesn't touch the skill name (that lives in SkillMetadataEditor).
  const liveCreateNameError = isCreating
    ? (() => {
        const trimmed = createForm.name.trim();
        return trimmed.length === 0 ? null : validateSkillName(trimmed);
      })()
    : null;

  const title = isCreating
    ? "Create Skill"
    : `Edit ${summary?.skill.name ?? ""}${target ? ` (v${target.version})` : ""}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={styles.formStack}>
          {isCreating ? (
            <>
              <TextField
                label="Name"
                value={createForm.name}
                onChange={(e) => updateCreate("name", e.target.value)}
                error={liveCreateNameError !== null}
                helperText={
                  liveCreateNameError ??
                  "Letters, digits, hyphens, and underscores. No spaces."
                }
                required
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel id="accessibility-label">Accessibility</InputLabel>
                <Select
                  labelId="accessibility-label"
                  label="Accessibility"
                  value={createForm.accessibility}
                  onChange={(e) =>
                    updateCreate(
                      "accessibility",
                      e.target.value as SkillAccessibility,
                    )
                  }
                >
                  <MenuItem value="private">Private (only you)</MenuItem>
                  <MenuItem value="org">
                    Org (everyone in your org can use it)
                  </MenuItem>
                  <MenuItem value="org-editable">
                    Org-editable (everyone in your org can use and edit it)
                  </MenuItem>
                </Select>
              </FormControl>
              <TagsAutocomplete
                value={createForm.tags}
                onChange={(next) => updateCreate("tags", next)}
                options={knownTagsQuery.data ?? []}
                helperText="Free-form labels for grouping skills (e.g. qa-review, experiments). Press Enter or click out to add."
              />
              <TextField
                label="When to use"
                value={createForm.description}
                onChange={(e) => updateCreate("description", e.target.value)}
                helperText={`Short description the AI uses to decide when to apply this skill (≤${SKILL_DESCRIPTION_MAX_LENGTH} chars).`}
                multiline
                minRows={2}
                inputProps={{ maxLength: SKILL_DESCRIPTION_MAX_LENGTH }}
                required
                fullWidth
              />
              <TextField
                label="Procedure / body"
                value={createForm.body}
                onChange={(e) => updateCreate("body", e.target.value)}
                helperText="Markdown supported — separate paragraphs with a blank line and use “- ” for lists."
                multiline
                minRows={6}
                inputProps={{ className: styles.bodyInput }}
                required
                fullWidth
              />
            </>
          ) : (
            <>
              <TextField
                label="When to use"
                value={editForm.description}
                onChange={(e) => updateEdit("description", e.target.value)}
                helperText={`Short description the AI uses to decide when to apply this skill (≤${SKILL_DESCRIPTION_MAX_LENGTH} chars).`}
                multiline
                minRows={2}
                inputProps={{ maxLength: SKILL_DESCRIPTION_MAX_LENGTH }}
                required
                fullWidth
              />
              <TextField
                label="Procedure / body"
                value={editForm.body}
                onChange={(e) => updateEdit("body", e.target.value)}
                helperText="Markdown supported — separate paragraphs with a blank line and use “- ” for lists."
                multiline
                minRows={6}
                inputProps={{ className: styles.bodyInput }}
                required
                fullWidth
              />
            </>
          )}
          {errorMessage && (
            <small className={styles.errorMessage}>{errorMessage}</small>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        {!isCreating && (
          <Button
            onClick={() => {
              void handleCreateNewVersion();
            }}
            disabled={isSaving}
          >
            Save as new version
          </Button>
        )}
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={isSaving || liveCreateNameError !== null}
          loading={isMutating}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
